'use client';

import { Dispatch, SetStateAction, use, useEffect, useState } from 'react';
import {
    Button,
    Modal,
    TextInput,
    Textarea,
    Divider,
    Checkbox,
    Stack,
    Switch
} from '@mantine/core';
import { Dropzone } from '../EditorText/Dropzone';
import { LinkFieldInput } from './LinkFieldInput';
import { parseJwt } from '@/src/helpers/decode-jwt';
import Cookies from 'js-cookie';
import { AddSliceModal } from './AddSlice/AddSliceModal';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import { FullSizeLoader } from '../Loader/FullSizeLoader';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import styles from './EditableSliceWrapper.module.scss'
import { authValidation } from '@/src/helpers/authValidation';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

type SliceField = {
    key: string;
    label: string;
    value: string | boolean;
};

type EditableSliceWrapperProps = {
    children: React.ReactNode;
    slice: Record<string, any>;
    sliceIndex: number;
    lang: 'fr' | 'en';
    pageKey: string;
};

export const EditableSliceWrapper = ({
    children,
    slice,
    sliceIndex,
    lang,
    pageKey,
}: EditableSliceWrapperProps) => {
    const [opened, setOpened] = useState(false);
    const [addModalOpened, setAddModalOpened] = useState(false);
    const [formValues, setFormValues] = useState<Record<string, any>>(structuredClone(slice));
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [disconnected, setDisconnected] = useState(false);
    const [validated, setValidated] = useState(false);
    const router = useRouter();

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (token) {
            try {
                const decoded = parseJwt(token);
                if (decoded?.role === 'superAdmin') {
                    setIsSuperAdmin(true);
                } else if (decoded.role === 'admin') {
                    setIsAdmin(true);
                }
            } catch (err) {
                Cookies.remove('authToken');
                window.location.reload();
                console.error('Invalid token', err);
            }
        }
    }, []);

    const editableFields = flattenFields(formValues);
    const isEditableType = slice.type !== 'space' && slice.type !== 'divider';

    const handleSave = async () => {
        const response = await fetch('/api/slices', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                lang,
                pageKey,
                sliceIndex,
                updates: formValues,
            }),
        });

        if (response.ok) {
            addDeploymentToast('Déploiement des slices en cours. Optimisation des fichiers...');
            setOpened(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm('Supprimer ce bloc ?')) return;
        const res = await fetch('/api/slices/delete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pageKey,
                sliceIndex,
                locale: lang,
            }),
        });

        if (res.ok) {
            addDeploymentToast('Suppression des slices en cours. Optimisation des fichiers...');
            setOpened(false);
        } else {
            alert('Erreur lors de la suppression');
        }
    };

    const handleAdd = async (slice: any) => {
        const res = await fetch('/api/slices/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                pageKey,
                newSlice: slice,
                locale: lang,
                position: sliceIndex + 1,
            }),
        });

        if (res.ok) {
            addDeploymentToast('Déploiement des nouvelles slices en cours. Optimisation des fichiers...');
            setAddModalOpened(false);
        } else {
            alert('Erreur lors de l’ajout du bloc');
        }
    };

    const handleChange = (path: string, value: string) => {
        console.log("handleChange", path, value)
        const updated = structuredClone(formValues);
        setDeepValue(updated, path, value);
        setFormValues(updated);
    };

    const handleOpen = async (value: boolean) => {
        const isValid = await authValidation();
        if (!isValid) {
            setDisconnected(true); // show modal
        } else {
            setValidated(true);
        }
        setOpened(value)
    }

    const handleDeleteClick = async () => {
        const isValid = await authValidation();
        if (!isValid) {
            setDisconnected(true); // show modal
        } else {
            setValidated(true);
        }
        handleDelete()
    }

    const handleOpenClick = async (value: boolean) => {
        const isValid = await authValidation();
        if (!isValid) {
            setDisconnected(true); // show modal
        } else {
            setValidated(true);
        }
        setAddModalOpened(value)
    }

    return (
        <div className={styles.EditableSliceWrapper}>
            {isEditableType && (isSuperAdmin || isAdmin) && editableFields.length > 0 && (
                <div className={styles.buttonsUpdate}>
                    <Button size="xs" color="black" onClick={() => handleOpen(true)}>
                        Modifier
                    </Button>
                    <Button size="xs" color="darkRed" onClick={handleDeleteClick}>
                        Supprimer
                    </Button>
                </div>
            )}

            {children}

            {isSuperAdmin && (
                <>
                    <div
                        onClick={() => handleOpenClick(true)}
                        style={{
                            cursor: 'pointer',
                            textAlign: 'center',
                            padding: '1rem 0',
                            color: '#aaa',
                            userSelect: 'none'
                        }}
                    >
                        <Divider label="+ Choisir l'élement à ajouter" labelPosition="center" />
                    </div>

                    <AddSliceModal
                        opened={addModalOpened}
                        onClose={() => setAddModalOpened(false)}
                        onAdd={handleAdd}
                    />
                </>
            )}

            <>
                <Modal
                    opened={disconnected}
                    onClose={() => {
                        Cookies.remove('authToken');
                        router.refresh();
                    }}
                    title="Session Expired"
                    centered
                >
                    <p>Your session has expired. Please log in again.</p>
                    <Button component={Link} href='/login'>Go to Login</Button>
                </Modal>
            </>

            <Modal opened={opened && !disconnected} onClose={() => setOpened(false)} title="Modifier ce bloc" size="lg" centered>
                {editableFields.map(({ key, label, value }) => {
                    const isImage = (key.toLowerCase().includes('image') && key.toLowerCase() !== "isimage") || key === 'src';
                    const isLink = /link|href/i.test(key);
                    const isDescription = key.toLowerCase().includes('description');

                    return (
                        <div key={key} style={{ marginBottom: 20 }}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>{label}</label>
                            {typeof value === 'boolean' ? (
                                <Checkbox
                                    label={label}
                                    checked={value}
                                    // @ts-ignore
                                    onChange={(e) => handleChange(key, e.currentTarget.checked)}
                                />
                            ) : isImage ? (
                                <Dropzone
                                    imageUrl={getCleanUrl(value)}
                                    // @ts-ignore
                                    setImageUrl={(url: string) => handleChange(key, url)}
                                />
                            ) : isLink ? (
                                <LinkFieldInput value={value} onChange={(val) => handleChange(key, val)} lang={lang} />
                            ) : key === 'version' ? (
                                <Switch
                                    label="Thème sombre"
                                    checked={value === 'dark'}
                                    onChange={(e) => handleChange(key, e.currentTarget.checked ? 'dark' : 'light')}
                                />
                            ) : isDescription ? (
                                <Textarea value={value} onChange={(e) => handleChange(key, e.currentTarget.value)} autosize minRows={3} />
                            ) : (
                                <TextInput value={value} onChange={(e) => handleChange(key, e.currentTarget.value)} />
                            )}
                        </div>
                    );
                })}

                <Button fullWidth mt="md" onClick={handleSave}>
                    Enregistrer
                </Button>
            </Modal>
        </div >
    );
};

// Helpers

function flattenFields(obj: any, prefix = ''): SliceField[] {
    const fields: SliceField[] = [];

    Object.entries(obj).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            fields.push({
                key: path,
                label: key,
                value: getCleanUrl(value),
            });
        } else if (typeof value === 'boolean') {
            fields.push({
                key: path,
                label: key,
                value: value,
            });
        } else if (Array.isArray(value)) {
            if (value.every((item) => typeof item === 'string')) {
                value.forEach((item, index) => {
                    fields.push({
                        key: `${path}[${index}]`,
                        label: `${key} ${index + 1}`,
                        value: getCleanUrl(item),
                    });
                });
            } else {
                value.forEach((item, index) => {
                    fields.push(...flattenFields(item, `${path}[${index}]`));
                });
            }
        } else if (typeof value === 'object' && value !== null) {
            fields.push(...flattenFields(value, path));
        }
    });

    return fields;
}


function getCleanUrl(value: string): string {
    try {
        const url = new URL(value, window !== undefined ? window?.location?.origin : '');
        if (url.pathname === '/api/image-proxy' && url.searchParams.has('url')) {
            return decodeURIComponent(url.searchParams.get('url') || '');
        }
    } catch (err) {
        console.log("error", err)
    }

    return value;
}


function setDeepValue(obj: any, path: string, value: any): void {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in current)) current[k] = {};
        current = current[k];
    }
    current[keys[keys.length - 1]] = value;
}

function getDeepValue(obj: any, path: string): any {
    const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    return keys.reduce((acc, key) => acc?.[key], obj);
}
